import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell,
  AreaChart, Area, CartesianGrid,
} from "recharts";
import {
  Inbox, CheckCircle2, Clock3, Trash2, RefreshCw, MessageCircle, Phone, MapPin,
  Calendar, TrendingUp, Loader2, Search, Download, Save, X, Languages,
} from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const STATUSES = ["all", "new", "contacted", "booked", "closed"];
const STATUS_COLORS = {
  new: "text-amber-800 bg-amber-100 border-amber-300",
  contacted: "text-blue-800 bg-blue-100 border-blue-300",
  booked: "text-green-800 bg-green-100 border-green-300",
  closed: "text-stone-700 bg-stone-100 border-stone-300",
};
const BAR_COLORS = ["#0E5A5E", "#B4232A", "#B4232A", "#4FA8A5", "#8E1B22", "#0E5A5E"];

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="glass rounded-2xl p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-tara-muted uppercase tracking-wider">{label}</span>
        <Icon className="h-5 w-5" style={{ color }} />
      </div>
      <p className="font-serif text-3xl font-bold text-tara-ink">{value}</p>
    </div>
  );
}

function NotesEditor({ enquiry, onSaved }) {
  const [value, setValue] = useState(enquiry.admin_notes || "");
  const [saving, setSaving] = useState(false);
  const dirty = value !== (enquiry.admin_notes || "");

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/enquiries/${enquiry.id}`, { admin_notes: value });
      toast.success("Note saved");
      onSaved(enquiry.id, value);
    } catch {
      toast.error("Could not save note");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3">
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        rows={2}
        placeholder="Private notes — what was discussed, next step, quoted amount…"
        className="w-full rounded-xl border border-tara-gold/25 bg-white/80 p-3 text-sm text-tara-ink placeholder:text-tara-muted/70 outline-none focus:border-tara-gold/60"
        data-testid={`notes-input-${enquiry.id}`}
      />
      {dirty && (
        <button
          onClick={save}
          disabled={saving}
          className="btn-sacred mt-2 inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold"
          data-testid={`notes-save-${enquiry.id}`}
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save note
        </button>
      )}
    </div>
  );
}

export default function EnquiriesPanel() {
  const [stats, setStats] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [loading, setLoading] = useState(true);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (filter) p.set("status", filter);
    if (search) p.set("q", search);
    if (from) p.set("date_from", from);
    if (to) p.set("date_to", to);
    return p.toString();
  }, [filter, search, from, to]);

  const statParams = useMemo(() => {
    const p = new URLSearchParams();
    if (from) p.set("date_from", from);
    if (to) p.set("date_to", to);
    return p.toString();
  }, [from, to]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, e] = await Promise.all([
        api.get(`/admin/stats?${statParams}`),
        api.get(`/admin/enquiries?${params}`),
      ]);
      setStats(s.data);
      setEnquiries(e.data);
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [params, statParams]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/enquiries/${id}`, { status });
      toast.success(`Marked as ${status}`);
      load();
    } catch { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/enquiries/${id}`);
      toast.success("Deleted");
      setEnquiries((prev) => prev.filter((x) => x.id !== id));
    } catch { toast.error("Delete failed"); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get(`/admin/enquiries/export?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch { toast.error("Export failed"); }
  };

  const clearFilters = () => { setFilter("all"); setQuery(""); setSearch(""); setFrom(""); setTo(""); };

  const serviceData = (stats?.by_service || []).slice(0, 6).map((s) => ({
    name: (s.service || "General").split(" ").slice(0, 2).join(" "),
    count: s.count,
  }));
  const langData = (stats?.by_language || []).map((l) => ({ name: l.language, count: l.count }));
  const trendData = (stats?.trend || []).slice(-30).map((t) => ({
    date: t.date.slice(5),
    count: t.count,
  }));

  return (
    <div data-testid="enquiries-panel">
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-5 mb-6">
        <StatCard icon={Inbox} label="Total" value={stats?.total ?? "—"} color="#0E5A5E" />
        <StatCard icon={Clock3} label="New" value={stats?.new ?? "—"} color="#B4232A" />
        <StatCard icon={MessageCircle} label="Contacted" value={stats?.contacted ?? "—"} color="#2563EB" />
        <StatCard icon={CheckCircle2} label="Booked" value={stats?.booked ?? "—"} color="#15803D" />
        <StatCard icon={TrendingUp} label="Last 7 Days" value={stats?.last_7_days ?? "—"} color="#B4232A" />
      </div>

      {/* Filters */}
      <div className="glass rounded-2xl p-5 mb-6 space-y-4">
        <div className="flex flex-col lg:flex-row gap-3">
          <form
            className="relative flex-1"
            onSubmit={(e) => { e.preventDefault(); setSearch(query); }}
          >
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-tara-muted" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, place, phone, concern or note…"
              className="w-full rounded-full border border-tara-gold/25 bg-white/85 py-2.5 pl-11 pr-28 text-sm text-tara-ink placeholder:text-tara-muted/70 outline-none focus:border-tara-gold/60"
              data-testid="enquiry-search-input"
            />
            <button
              type="submit"
              className="btn-sacred absolute right-1.5 top-1/2 -translate-y-1/2 rounded-full px-4 py-1.5 text-xs font-semibold"
              data-testid="enquiry-search-btn"
            >
              Search
            </button>
          </form>
          <div className="flex items-center gap-2">
            <input
              type="date" value={from} onChange={(e) => setFrom(e.target.value)}
              className="rounded-full border border-tara-gold/25 bg-white/85 px-4 py-2.5 text-sm text-tara-ink outline-none focus:border-tara-gold/60"
              data-testid="enquiry-date-from"
            />
            <span className="text-tara-muted text-xs">to</span>
            <input
              type="date" value={to} onChange={(e) => setTo(e.target.value)}
              className="rounded-full border border-tara-gold/25 bg-white/85 px-4 py-2.5 text-sm text-tara-ink outline-none focus:border-tara-gold/60"
              data-testid="enquiry-date-to"
            />
          </div>
          <div className="flex items-center gap-2">
            <button onClick={exportCsv} className="btn-ghost-gold flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold" data-testid="enquiry-export-btn">
              <Download className="h-4 w-4" /> CSV
            </button>
            <button onClick={load} className="rounded-full border border-tara-gold/30 p-2.5 text-tara-gold hover:bg-tara-gold/10 transition" data-testid="admin-refresh-btn" aria-label="Refresh">
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-wider transition ${
                filter === s
                  ? "bg-gradient-to-r from-tara-gold to-tara-vermilion text-white"
                  : "border border-tara-gold/25 text-tara-body hover:text-tara-gold"
              }`}
              data-testid={`filter-${s}`}
            >
              {s}
            </button>
          ))}
          {(search || from || to || filter !== "all") && (
            <button onClick={clearFilters} className="flex items-center gap-1 text-xs text-tara-muted hover:text-tara-sindoor ml-1" data-testid="enquiry-clear-filters">
              <X className="h-3.5 w-3.5" /> clear
            </button>
          )}
          <span className="ml-auto text-xs text-tara-muted" data-testid="enquiry-result-count">
            {enquiries.length} result{enquiries.length === 1 ? "" : "s"}
          </span>
        </div>
      </div>

      {/* Breakdowns */}
      <div className="grid gap-5 lg:grid-cols-3 mb-8">
        {serviceData.length > 0 && (
          <div className="glass rounded-2xl p-5 lg:col-span-2" data-testid="chart-by-service">
            <h3 className="font-serif text-sm uppercase tracking-[0.18em] text-tara-goldDeep mb-4">Enquiries by service</h3>
            <ResponsiveContainer width="100%" height={210}>
              <BarChart data={serviceData}>
                <XAxis dataKey="name" tick={{ fill: "#7C8F92", fontSize: 11 }} interval={0} angle={-12} textAnchor="end" height={50} />
                <YAxis allowDecimals={false} tick={{ fill: "#7C8F92", fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid rgba(14,90,94,0.3)", borderRadius: 12, color: "#14262A" }} cursor={{ fill: "rgba(14,90,94,0.06)" }} />
                <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                  {serviceData.map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        <div className="glass rounded-2xl p-5" data-testid="chart-by-language">
          <h3 className="font-serif text-sm uppercase tracking-[0.18em] text-tara-goldDeep mb-4 flex items-center gap-2">
            <Languages className="h-3.5 w-3.5" /> By language
          </h3>
          {langData.length === 0 ? (
            <p className="text-sm text-tara-muted">No data yet.</p>
          ) : (
            <ul className="space-y-3">
              {langData.map((l, i) => {
                const max = Math.max(...langData.map((x) => x.count));
                return (
                  <li key={l.name}>
                    <div className="flex justify-between text-xs text-tara-body mb-1">
                      <span>{l.name}</span><span className="font-semibold">{l.count}</span>
                    </div>
                    <div className="h-2 rounded-full bg-tara-sand overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${(l.count / max) * 100}%`, background: BAR_COLORS[i % BAR_COLORS.length] }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {trendData.length > 1 && (
        <div className="glass rounded-2xl p-5 mb-8" data-testid="chart-trend">
          <h3 className="font-serif text-sm uppercase tracking-[0.18em] text-tara-goldDeep mb-4">Daily enquiries</h3>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={trendData}>
              <defs>
                <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0E5A5E" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#0E5A5E" stopOpacity={0.04} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(14,90,94,0.14)" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: "#7C8F92", fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fill: "#7C8F92", fontSize: 11 }} />
              <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid rgba(14,90,94,0.3)", borderRadius: 12, color: "#14262A" }} />
              <Area type="monotone" dataKey="count" stroke="#B4232A" strokeWidth={2} fill="url(#trendFill)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 text-tara-gold animate-spin" /></div>
      ) : enquiries.length === 0 ? (
        <div className="glass rounded-2xl p-12 text-center text-tara-muted" data-testid="admin-empty">
          <Inbox className="h-10 w-10 mx-auto mb-3 text-tara-muted/70" />
          No enquiries match this view.
        </div>
      ) : (
        <div className="space-y-4" data-testid="enquiry-list">
          {enquiries.map((e) => (
            <div key={e.id} className="glass rounded-2xl p-5" data-testid={`enquiry-${e.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display text-xl font-semibold text-tara-ink">{e.name}</h3>
                    <span className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_COLORS[e.status] || STATUS_COLORS.closed}`}>{e.status}</span>
                    <span className="rounded-full bg-white/70 border border-tara-gold/20 px-2.5 py-0.5 text-[10px] text-tara-body uppercase">{e.language}</span>
                  </div>
                  <p className="text-sm text-tara-goldDeep mt-1">{e.service_title || "General Enquiry"}</p>
                </div>
                <div className="flex items-center gap-2">
                  {e.phone && (
                    <a href={`https://wa.me/${e.phone.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="rounded-full border border-green-500/40 p-2 text-green-600 hover:bg-green-500/10 transition" aria-label="WhatsApp">
                      <MessageCircle className="h-4 w-4" />
                    </a>
                  )}
                  <button onClick={() => remove(e.id)} className="rounded-full border border-red-500/40 p-2 text-red-600 hover:bg-red-500/10 transition" data-testid={`delete-${e.id}`} aria-label="Delete">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {e.concern && <p className="text-sm text-tara-body leading-relaxed mb-3 bg-[#F5FAF9] rounded-xl p-3">{e.concern}</p>}

              <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-tara-muted mb-3">
                {e.location && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {e.location}</span>}
                {e.phone && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {e.phone}</span>}
                {e.birth_details && <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {e.birth_details}</span>}
                <span className="flex items-center gap-1"><Clock3 className="h-3.5 w-3.5" /> {new Date(e.created_at).toLocaleString()}</span>
              </div>

              <div className="flex flex-wrap gap-2">
                {["new", "contacted", "booked", "closed"].map((st) => (
                  <button
                    key={st}
                    onClick={() => updateStatus(e.id, st)}
                    disabled={e.status === st}
                    className={`rounded-lg px-3 py-1.5 text-xs font-medium transition ${
                      e.status === st
                        ? "bg-tara-gold/20 text-tara-goldDeep cursor-default"
                        : "border border-tara-gold/20 text-tara-muted hover:text-tara-gold hover:border-tara-gold/50"
                    }`}
                    data-testid={`status-${st}-${e.id}`}
                  >
                    {st}
                  </button>
                ))}
              </div>

              <NotesEditor
                enquiry={e}
                onSaved={(id, notes) =>
                  setEnquiries((prev) => prev.map((x) => (x.id === id ? { ...x, admin_notes: notes } : x)))
                }
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
